// Splits a methodology HTML string into ordered segments so the UI can flag which
// parts came from an AI ingest. AiIngestFacade.appendMethodology (Java) wraps each
// AI-generated contribution in these markers before saving, and preserves whatever
// else (other targets' sections, human-typed text) was already there — so one
// scope's box can contain several AI blocks for different targets, interleaved
// with manual text.
const AI_SECTION_RE = /<!--\s*ai-ingest:target=([A-Za-z0-9_]+)\s*-->([\s\S]*?)<!--\s*\/ai-ingest\s*-->/g;

export function parseMethodologySections(html) {
  if (!html) return [];

  const sections = [];
  let lastIndex = 0;
  let match;

  AI_SECTION_RE.lastIndex = 0;
  while ((match = AI_SECTION_RE.exec(html)) !== null) {
    const manual = html.slice(lastIndex, match.index);
    if (manual.trim()) sections.push({ type: "manual", html: manual });
    sections.push({ type: "ai", target: match[1], html: match[2] });
    lastIndex = AI_SECTION_RE.lastIndex;
  }

  const tail = html.slice(lastIndex);
  if (tail.trim()) sections.push({ type: "manual", html: tail });

  return sections;
}

// AiIngestFacade.outcomeParagraph (Java) always opens an AI segment with this literal
// "<strong>AI-assisted ingest</strong> — " label. The chip above the segment now says
// the same thing, so strip it from display rather than showing it twice.
const AI_LABEL_PREFIX_RE = /^(\s*<p>\s*)<strong>\s*AI-assisted ingest\s*<\/strong>\s*—\s*/i;

export function stripAiAssistedIngestLabel(html) {
  if (!html) return html;
  return html.replace(AI_LABEL_PREFIX_RE, "$1");
}

// outcomeParagraph (Java) appends " Committed <yyyy-MM-dd>." as its own trailing sentence,
// only when the run has a createdDate. Pull the date out so it can render next to the chip
// (Edit Date row's clock icon) instead of buried at the end of the paragraph.
const COMMITTED_DATE_RE = /\s*Committed\s+(\d{4}-\d{2}-\d{2})\.\s*/;

export function extractCommittedDate(html) {
  if (!html) return { date: null, html };
  const match = html.match(COMMITTED_DATE_RE);
  if (!match) return { date: null, html };
  return { date: match[1], html: html.replace(COMMITTED_DATE_RE, "") };
}

// Matches the "Edit Date" row's own formatting (OrganizationQuestionnaire.jsx) — parsed
// manually rather than `new Date("yyyy-MM-dd")` so a UTC-behind locale can't roll the day
// back by one.
export function formatCommittedDate(dateString) {
  if (!dateString) return null;
  const match = /^(\d{4})-(\d{2})-(\d{2})$/.exec(dateString);
  if (!match) return dateString;
  const [, year, month, day] = match;
  const date = new Date(Number(year), Number(month) - 1, Number(day));
  return date.toLocaleDateString("en-GB", { day: "numeric", month: "short", year: "numeric" });
}

// render_task_summary (Python, data-sorting/backend/app/session/trail.py) always opens the
// derivation with this literal "<p><strong>Source</strong>: <filename>( (SHA-256 <code>…</code>))?</p>"
// line. Pull it out so the filename can render as its own file row (Document Sources styling)
// under the chip instead of as inline paragraph text.
const SOURCE_LINE_RE =
  /<p>\s*<strong>\s*Source\s*<\/strong>\s*:\s*([^<]*?)(?:\s*\(SHA-256\s*<code>([^<]*)<\/code>\))?\s*<\/p>/i;

function unescapeHtml(value) {
  return value
    .replace(/&amp;/g, "&")
    .replace(/&lt;/g, "<")
    .replace(/&gt;/g, ">")
    .replace(/&quot;/g, '"')
    .replace(/&#39;/g, "'");
}

export function extractSource(html) {
  if (!html) return { filename: null, digest: null, html };
  const match = html.match(SOURCE_LINE_RE);
  if (!match) return { filename: null, digest: null, html };
  const filename = unescapeHtml(match[1].trim());
  const digest = match[2] ? unescapeHtml(match[2].trim()) : null;
  return { filename, digest, html: html.replace(match[0], "") };
}

// derivation() (Java) always closes an AI segment with this literal pointer paragraph, whether
// or not a client sent a summary — "run.getId()" is the ai_ingest_run row, the same id
// AiIngestHistory.jsx lists below the card. Pull the id out to show next to the commit date
// rather than at the end of a full sentence.
const RUN_REFERENCE_RE =
  /<p>\s*Full trail, including the code for each step and the verification fingerprints:\s*ai-ingest run #(\d+)\.\s*<\/p>/i;

export function extractRunReference(html) {
  if (!html) return { runId: null, html };
  const match = html.match(RUN_REFERENCE_RE);
  if (!match) return { runId: null, html };
  return { runId: match[1], html: html.replace(match[0], "") };
}

// outcomeParagraph's rows() helper (Java) always writes "<count> row" / "<count> rows" — bold
// the pair so the figures that changed are scannable without reading the whole sentence.
const ROW_COUNT_RE = /(\d+)(\s+rows?\b)/gi;

export function boldRowCounts(html) {
  if (!html) return html;
  return html.replace(ROW_COUNT_RE, "<strong>$1$2</strong>");
}

// _transformation_list (Python) renders a plain "<strong>Transformations applied</strong>"
// heading (no colon) followed by a numbered <ol>. The step order isn't meaningful — the API
// already narrowed the list to "the steps that were a decision", not literal sequence — so swap
// the numbers for a bullet, and add the colon the heading was missing.
//
// Deliberately just swaps the tag, with no inline style and no injected bullet character/span:
// TipTap re-parses this HTML through its own ProseMirror schema, which keeps <ul>/<li> (it knows
// those nodes) but drops arbitrary style attributes and unwraps unrecognized <span> wrappers down
// to plain text — an earlier version injected its own "•" span for color control and ended up
// with two bullets (TipTap's native marker plus the leftover, now-unstyled, text character). The
// green comes from CSS on the wrapping element instead (MethodologyCard's `::marker` rule).
export function restyleTransformations(html) {
  if (!html) return html;
  return html
    .replace(/<strong>\s*Transformations applied\s*<\/strong>/i, "<strong>Transformations applied:</strong>")
    .replace(/<ol>/gi, "<ul>")
    .replace(/<\/ol>/gi, "</ul>");
}
